/**
 * Brand video switcher: centre card, opacity/content state, and main hero media swap
 * (hosted video, iframe embed, or static image per tab).
 * Hosted clips: optional idle preloads (first desktop + mobile source per tab) to warm cache.
 */

const preloadedSwitcherVideoUrls = new Set();

const BRAND_VIDEO_DESKTOP_MQ = '(min-width: 1100px)';

function shouldSkipSwitcherVideoPreload() {
  const c = typeof navigator !== 'undefined' ? navigator.connection : null;
  if (!c) return false;
  if (c.saveData === true) return true;
  const t = c.effectiveType;
  return t === 'slow-2g' || t === '2g';
}

function scheduleIdlePreload(fn) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => fn(), { timeout: 2800 });
  } else {
    window.setTimeout(fn, 200);
  }
}

/**
 * @param {string} url
 * @param {string} [mimeType]
 */
function appendVideoPreloadLink(url, mimeType) {
  if (!url || preloadedSwitcherVideoUrls.has(url)) return;
  preloadedSwitcherVideoUrls.add(url);
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'video';
  link.href = url;
  if (mimeType) link.setAttribute('type', mimeType);
  document.head.appendChild(link);
}

/**
 * Hint the browser to fetch each switcher’s primary Shopify file (desktop + distinct mobile).
 * @param {HTMLElement[]} switchers
 */
function preloadHostedVideosFromSwitchers(switchers) {
  if (!switchers?.length || shouldSkipSwitcherVideoPreload()) return;

  for (const el of switchers) {
    const desktop = readSourcesAttr(el, 'data-desktop-sources');
    const mobile = readSourcesAttr(el, 'data-mobile-sources');

    if (desktop.length) {
      const first = desktop[0];
      appendVideoPreloadLink(
        normalizeMediaUrl(sourceEntryUrl(first)),
        first.mime_type || first.mimeType || '',
      );
    }

    if (mobile.length) {
      const firstM = mobile[0];
      const mUrl = normalizeMediaUrl(sourceEntryUrl(firstM));
      const dUrl =
        desktop.length > 0
          ? normalizeMediaUrl(sourceEntryUrl(desktop[0]))
          : '';
      if (mUrl && mUrl !== dUrl) {
        appendVideoPreloadLink(
          mUrl,
          firstM.mime_type || firstM.mimeType || '',
        );
      }
    }
  }
}

/**
 * @param {number} activeIndex
 * @param {number} n
 * @returns {number[]}
 */
function visualPermutation(activeIndex, n) {
  if (n <= 1) return [0];
  if (n === 2) {
    return activeIndex === 0 ? [0, 1] : [1, 0];
  }
  if (n === 3) {
    const others = [0, 1, 2].filter((i) => i !== activeIndex);
    others.sort((a, b) => a - b);
    if (activeIndex === 0) {
      return [others[1], activeIndex, others[0]];
    }
    return [others[0], activeIndex, others[1]];
  }
  const rest = [...Array(n).keys()].filter((i) => i !== activeIndex);
  const mid = Math.floor(n / 2);
  return [...rest.slice(0, mid), activeIndex, ...rest.slice(mid)];
}

function parseSources(raw) {
  if (raw == null || raw === '') return [];
  const str = typeof raw === 'string' ? raw.trim() : String(raw);
  try {
    const v = JSON.parse(str || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function readSourcesAttr(el, attrName) {
  const fromAttr = el.getAttribute(attrName);
  if (fromAttr != null && fromAttr !== '') return parseSources(fromAttr);
  const camel = attrName
    .slice('data-'.length)
    .replace(/-([a-z])/g, (_, c) => c.toUpperCase());
  return parseSources(el.dataset[camel]);
}

function readTrimmedAttr(el, attrName) {
  const v = el.getAttribute(attrName);
  return v == null ? '' : String(v).trim();
}

/**
 * @param {HTMLElement} el
 * @returns {'video' | 'image'}
 */
function getSwitcherMediaType(el) {
  const t = readTrimmedAttr(el, 'data-media-type').toLowerCase();
  return t === 'image' ? 'image' : 'video';
}

function hasSwitcherImage(el) {
  return readTrimmedAttr(el, 'data-image-desktop-url').length > 0;
}

function hideHeroImage(main) {
  const layer = main.querySelector('[data-bvs-image-layer]');
  const hosted = main.querySelector('[data-bvs-hosted-layer]');
  if (layer) {
    layer.classList.add('hidden');
    layer.setAttribute('aria-hidden', 'true');
  }
  if (hosted) {
    hosted.classList.remove('bvs-hosted-layer--hidden-for-image');
  }
}

/**
 * @param {HTMLElement} main
 * @param {HTMLElement} switcherEl
 * @returns {boolean}
 */
function swapHeroImage(main, switcherEl) {
  const desk = readTrimmedAttr(switcherEl, 'data-image-desktop-url');
  const mob = readTrimmedAttr(switcherEl, 'data-image-mobile-url') || desk;
  if (!desk) return false;

  pauseContainerMedia(main);
  applyPoster(main, desk);

  const hosted = main.querySelector('[data-bvs-hosted-layer]');
  const layer = main.querySelector('[data-bvs-image-layer]');
  const imgD = main.querySelector('[data-bvs-hero-img-desktop]');
  const imgM = main.querySelector('[data-bvs-hero-img-mobile]');
  const alt = readTrimmedAttr(switcherEl, 'data-image-alt');
  const srcsetD = readTrimmedAttr(switcherEl, 'data-image-srcset-desktop');
  const srcsetM = readTrimmedAttr(switcherEl, 'data-image-srcset-mobile');
  const w = switcherEl.getAttribute('data-image-width');
  const h = switcherEl.getAttribute('data-image-height');

  if (imgD) {
    imgD.src = normalizeMediaUrl(desk) || desk;
    if (srcsetD) imgD.setAttribute('srcset', srcsetD);
    else imgD.removeAttribute('srcset');
    imgD.alt = alt;
    imgD.sizes = '(min-width: 768px) 100vw, 1px';
    if (w) imgD.setAttribute('width', w);
    if (h) imgD.setAttribute('height', h);
  }
  if (imgM) {
    imgM.src = normalizeMediaUrl(mob) || mob;
    if (srcsetM) imgM.setAttribute('srcset', srcsetM);
    else imgM.removeAttribute('srcset');
    imgM.alt = alt;
    imgM.sizes = '(max-width: 767px) 100vw, 1px';
    if (w) imgM.setAttribute('width', w);
    if (h) imgM.setAttribute('height', h);
  }

  if (hosted) hosted.classList.add('bvs-hosted-layer--hidden-for-image');
  if (layer) {
    layer.classList.remove('hidden');
    layer.setAttribute('aria-hidden', alt ? 'false' : 'true');
  }
  return true;
}

function normalizeMediaUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('//')) return `https:${url}`;
  return url;
}

function sourceEntryUrl(entry) {
  if (!entry || typeof entry !== 'object') return '';
  return entry.url || entry.URL || entry.src || '';
}

/**
 * @param {HTMLVideoElement} video
 * @param {object[]} sources
 * @param {string} [posterUrl] - preview image for native video poster (matches switcher card)
 */
function setVideoElementSources(video, sources, posterUrl) {
  if (!video || !sources?.length) return;
  video.removeAttribute('src');
  video.innerHTML = '';
  for (const s of sources) {
    const url = normalizeMediaUrl(sourceEntryUrl(s));
    if (!url) continue;
    const source = document.createElement('source');
    source.src = url;
    source.type = s.mime_type || s.mimeType || 'video/mp4';
    video.appendChild(source);
  }
  const poster = typeof posterUrl === 'string' ? posterUrl.trim() : '';
  if (poster) {
    const p = normalizeMediaUrl(poster);
    video.poster = p;
    video.setAttribute('poster', p);
  } else {
    video.removeAttribute('poster');
  }
  video.muted = true;
  video.setAttribute('muted', '');
  video.load();
  const wrap = video.closest('[data-lazy-video]');
  if (wrap) {
    wrap.classList.add('loaded');
    wrap.style.opacity = '1';
  }
  if (video.hasAttribute('autoplay')) {
    video.play().catch(() => {});
  }
}

function pauseContainerMedia(container) {
  container.querySelectorAll('video').forEach((v) => v.pause());
}

/**
 * Updates all blur placeholders under the hero (desktop/mobile layouts can repeat the pattern).
 * Clears previous art when the new clip has no preview URL.
 */
function applyPoster(container, posterUrl) {
  const blurs = container.querySelectorAll('[data-blur-placeholder]');
  if (!blurs.length) return;
  const raw = typeof posterUrl === 'string' ? posterUrl.trim() : '';
  const safe = raw.replace(/'/g, "\\'");
  blurs.forEach((blur) => {
    if (raw) {
      blur.style.backgroundImage = `url('${safe}')`;
    } else {
      blur.style.removeProperty('background-image');
    }
  });
}

function swapHostedVideo(container, switcherEl) {
  const desktop = readSourcesAttr(switcherEl, 'data-desktop-sources');
  const mobile = readSourcesAttr(switcherEl, 'data-mobile-sources');
  if (!desktop.length) return false;

  pauseContainerMedia(container);
  const posterRaw = switcherEl.getAttribute('data-poster-url') || '';
  applyPoster(container, posterRaw);

  const wrappers = [...container.querySelectorAll('[data-lazy-video]')];
  const vDesk = wrappers[0]?.querySelector('video');
  const vMob = wrappers[1]?.querySelector('video');
  const mobSources = mobile.length ? mobile : desktop;
  setVideoElementSources(vDesk, desktop, posterRaw);
  setVideoElementSources(vMob, vMob && vMob !== vDesk ? mobSources : desktop, posterRaw);
  container.querySelectorAll('video').forEach((v) => {
    v.muted = true;
    v.play().catch(() => {});
  });
  return true;
}

function swapIframeVideo(container, switcherEl) {
  const src = (
    switcherEl.getAttribute('data-iframe-src') ||
    switcherEl.dataset.iframeSrc ||
    ''
  ).trim();
  if (!src) return false;
  const iframe = container.querySelector('.content-video iframe, iframe');
  if (!iframe) return false;
  pauseContainerMedia(container);
  applyPoster(
    container,
    switcherEl.getAttribute('data-poster-url') || '',
  );
  iframe.src = src;
  return true;
}

function nextMainVideoSwapToken(root) {
  const t = (parseInt(root.dataset.bvsSwapToken, 10) || 0) + 1;
  root.dataset.bvsSwapToken = String(t);
  return t;
}

function isMainVideoSwapCurrent(root, tokenAtStart) {
  return parseInt(root.dataset.bvsSwapToken, 10) === tokenAtStart;
}

/**
 * Swaps media inside [data-main-video] immediately (no fade).
 * @param {HTMLElement} root
 * @param {HTMLElement} switcherEl
 */
function applyMainVideoInstant(root, switcherEl) {
  const main = root.querySelector('[data-main-video]');
  if (!main || !switcherEl) return;

  if (getSwitcherMediaType(switcherEl) === 'image' && hasSwitcherImage(switcherEl)) {
    if (swapHeroImage(main, switcherEl)) return;
  }

  hideHeroImage(main);

  const desktop = readSourcesAttr(switcherEl, 'data-desktop-sources');
  const iframeSrc = (
    switcherEl.getAttribute('data-iframe-src') ||
    switcherEl.dataset.iframeSrc ||
    ''
  ).trim();

  if (desktop.length > 0) {
    swapHostedVideo(main, switcherEl);
    return;
  }
  if (iframeSrc) {
    swapIframeVideo(main, switcherEl);
  }
}

const FADE_OUT_MS = 320;

/**
 * Fade out → swap → fade in when the new clip can play (with timeouts as fallback).
 * @param {HTMLElement} root
 * @param {HTMLElement} switcherEl
 */
function runMainVideoSwapWithFade(root, switcherEl) {
  const main = root.querySelector('[data-main-video]');
  if (!main || !switcherEl) return;

  const desktop = readSourcesAttr(switcherEl, 'data-desktop-sources');
  const iframeSrc = (
    switcherEl.getAttribute('data-iframe-src') ||
    switcherEl.dataset.iframeSrc ||
    ''
  ).trim();

  const isImageTab =
    getSwitcherMediaType(switcherEl) === 'image' && hasSwitcherImage(switcherEl);

  if (!desktop.length && !iframeSrc && !isImageTab) return;

  const tokenAtStart = nextMainVideoSwapToken(root);
  main.classList.add('brand-video-switcher__main--fading');

  window.setTimeout(() => {
    if (!isMainVideoSwapCurrent(root, tokenAtStart)) return;

    const reveal = () => {
      if (!isMainVideoSwapCurrent(root, tokenAtStart)) return;
      main.classList.remove('brand-video-switcher__main--fading');
    };

    if (isImageTab) {
      const ok = swapHeroImage(main, switcherEl);
      if (!ok) {
        reveal();
        return;
      }
      const primary =
        typeof window.matchMedia === 'function' &&
        window.matchMedia(BRAND_VIDEO_DESKTOP_MQ).matches
          ? main.querySelector('[data-bvs-hero-img-desktop]')
          : main.querySelector('[data-bvs-hero-img-mobile]');
      const kick = () => {
        if (!isMainVideoSwapCurrent(root, tokenAtStart)) return;
        reveal();
      };
      if (primary) {
        if (primary.complete && primary.naturalWidth > 0) {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(kick);
          });
        } else {
          primary.addEventListener('load', kick, { once: true });
          primary.addEventListener('error', kick, { once: true });
        }
      } else {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(kick);
        });
      }
      window.setTimeout(kick, 2400);
      return;
    }

    hideHeroImage(main);

    if (desktop.length > 0) {
      swapHostedVideo(main, switcherEl);
    } else {
      swapIframeVideo(main, switcherEl);
    }

    if (desktop.length > 0) {
      const videos = [...main.querySelectorAll('.content-video video')];
      let pending = videos.length;
      if (pending === 0) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(reveal);
        });
        return;
      }
      const tick = () => {
        pending -= 1;
        if (pending <= 0) reveal();
      };
      for (const v of videos) {
        if (v.readyState >= 2) {
          tick();
        } else {
          v.addEventListener('loadeddata', tick, { once: true });
          v.addEventListener('error', tick, { once: true });
        }
      }
      window.setTimeout(reveal, 2600);
    } else {
      const iframe = main.querySelector('.content-video iframe, iframe');
      if (iframe) {
        iframe.addEventListener('load', reveal, { once: true });
        window.setTimeout(reveal, 3600);
      } else {
        window.setTimeout(reveal, 120);
      }
    }
  }, FADE_OUT_MS);
}

function setSwitcherActiveState(switchers, activeIndex) {
  switchers.forEach((el, i) => {
    const on = i === activeIndex;
    if (on) {
      el.setAttribute('data-active', '');
      el.setAttribute('aria-pressed', 'true');
    } else {
      el.removeAttribute('data-active');
      el.setAttribute('aria-pressed', 'false');
    }
  });
}

function applyDesktopRowOrder(rowItems, activeIndex) {
  const n = rowItems.length;
  const perm = visualPermutation(activeIndex, n);
  rowItems.forEach((rowEl, domIndex) => {
    rowEl.style.order = String(perm.indexOf(domIndex));
  });
}

function syncHeroVideo(root, switchers, activeIndex, instantVideo) {
  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (instantVideo || reduceMotion) {
    applyMainVideoInstant(root, switchers[activeIndex]);
    return;
  }
  runMainVideoSwapWithFade(root, switchers[activeIndex]);
}

/**
 * @param {boolean} [instantVideo] - first paint / reduced motion: skip fade
 * @param {boolean} [useDesktopLayout] - flex order (md+); false when Swiper owns slides
 */
function applyActiveState(
  root,
  rowItems,
  switchers,
  activeIndex,
  instantVideo,
  useDesktopLayout,
) {
  setSwitcherActiveState(switchers, activeIndex);
  if (useDesktopLayout) {
    applyDesktopRowOrder(rowItems, activeIndex);
  } else {
    rowItems.forEach((el) => el.style.removeProperty('order'));
  }
  syncHeroVideo(root, switchers, activeIndex, instantVideo);
}

function cssEscapeAttr(s) {
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(s)
    : s.replace(/["\\]/g, '\\$&');
}

/**
 * Slider Block ID in theme may be the JSON key (e.g. video_switcher_…) or a longer editor id ending in __sameKey.
 * @param {string} settingId
 * @param {string} blockId from data-block-id on the swiper row
 */
function sliderControlIdsMatch(settingId, blockId) {
  const a = (settingId || '').trim();
  const b = (blockId || '').trim();
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.endsWith(`__${b}`)) return true;
  if (b.endsWith(`__${a}`)) return true;
  const tail = (x) => (x.includes('__') ? x.split('__').pop() : x) || '';
  return tail(a) === tail(b);
}

/**
 * @param {HTMLElement} row
 * @returns {HTMLElement | null}
 */
function findBrandSwitcherControls(row) {
  const blockId = (row.getAttribute('data-block-id') || '').trim();
  if (!blockId) return null;
  const section = row.closest('[id^="shopify-section-"]');
  if (!section) return null;
  const exact = section.querySelector(
    `[data-slider-control][data-slider-id="${cssEscapeAttr(blockId)}"]`,
  );
  if (exact) return exact;
  for (const el of section.querySelectorAll('[data-slider-control][data-slider-id]')) {
    const sid = (el.getAttribute('data-slider-id') || '').trim();
    if (sliderControlIdsMatch(sid, blockId)) return el;
  }
  return null;
}

/**
 * @param {HTMLElement | null} controls
 * @returns {{ prevEl?: HTMLElement; nextEl?: HTMLElement } | null}
 */
function resolveBrandSwiperNavigationFromControls(controls) {
  if (!controls) return null;
  const nextBtn = controls.querySelector('.slider-navigation-next');
  const prevBtn = controls.querySelector('.slider-navigation-prev');
  if (!nextBtn && !prevBtn) return null;
  /** @type {{ prevEl?: HTMLElement; nextEl?: HTMLElement }} */
  const nav = {};
  if (prevBtn) nav.prevEl = prevBtn;
  if (nextBtn) nav.nextEl = nextBtn;
  return nav;
}

/**
 * Theme slider-controls pagination → Swiper (mobile carousel only).
 * @param {HTMLElement | null} controls
 * @returns {{ pagination: object; fractionEl: HTMLElement | null } | null}
 */
function buildSwiperPaginationFromControls(controls) {
  if (!controls) return null;
  const paginationEl = controls.querySelector('.swiper-pagination');
  if (!paginationEl) return null;
  const type = paginationEl.dataset.paginationType || 'bullets';

  if (type === 'progressbar_fraction') {
    const fractionEl =
      paginationEl.parentElement?.querySelector('[data-slider-fraction]') ||
      controls.querySelector('[data-slider-fraction]');
    return {
      pagination: {
        el: paginationEl,
        type: 'progressbar',
        clickable: false,
      },
      fractionEl,
    };
  }
  if (type === 'progressbar') {
    return {
      pagination: {
        el: paginationEl,
        type: 'progressbar',
        clickable: false,
      },
      fractionEl: null,
    };
  }
  if (type === 'bullets') {
    return {
      pagination: {
        el: paginationEl,
        type: 'bullets',
        clickable: true,
      },
      fractionEl: null,
    };
  }
  if (type === 'fraction') {
    return {
      pagination: {
        el: paginationEl,
        type: 'fraction',
      },
      fractionEl: null,
    };
  }
  return null;
}

/**
 * @param {HTMLElement} row
 * @returns {{ rowItems: HTMLElement[], switchers: HTMLElement[] } | null}
 */
function collectRowItemsAndSwitchers(row) {
  const wrapper = row.querySelector('.swiper-wrapper');
  if (wrapper) {
    const rowItems = [...wrapper.querySelectorAll(':scope > .swiper-slide')];
    const switchers = rowItems
      .map((item) =>
        item.matches('[data-video-switcher-button]')
          ? item
          : item.querySelector('[data-video-switcher-button]'),
      )
      .filter(Boolean);
    if (switchers.length > 0 && switchers.length === rowItems.length) {
      return { rowItems, switchers };
    }
  }

  const rowItems = [...row.children].filter(
    (el) => el.nodeType === Node.ELEMENT_NODE,
  );
  const switchers = rowItems
    .map((item) =>
      item.matches('[data-video-switcher-button]')
        ? item
        : item.querySelector('[data-video-switcher-button]'),
    )
    .filter(Boolean);

  if (switchers.length === 0 || switchers.length !== rowItems.length) {
    return null;
  }
  return { rowItems, switchers };
}

/**
 * @param {HTMLElement} root
 */
function initBrandVideoSwitcher(root) {
  if (root.dataset.brandVideoSwitcherInit === 'true') return;
  const row = root.querySelector('[data-brand-video-switcher-buttons]');
  if (!row) return;

  const collected = collectRowItemsAndSwitchers(row);
  if (!collected) return;

  const { rowItems, switchers } = collected;

  root.dataset.brandVideoSwitcherInit = 'true';

  const mql =
    typeof window.matchMedia === 'function'
      ? window.matchMedia(BRAND_VIDEO_DESKTOP_MQ)
      : { matches: true, addEventListener: () => {} };

  const isDesktop = () => mql.matches;

  let activeIndex = switchers.findIndex((el) =>
    el.hasAttribute('data-default-active'),
  );
  if (activeIndex < 0) {
    activeIndex = switchers.findIndex((el) => el.hasAttribute('data-active'));
  }
  if (activeIndex < 0) activeIndex = 0;

  /** @type {object | null} */
  let swiperInstance = null;

  function destroySwiper() {
    if (swiperInstance) {
      swiperInstance.destroy(true, true);
      swiperInstance = null;
    }
  }

  function ensureSwiperMobile() {
    if (isDesktop()) {
      destroySwiper();
      applyDesktopRowOrder(rowItems, activeIndex);
      return;
    }

    if (typeof window.Swiper !== 'function') {
      rowItems.forEach((el) => el.style.removeProperty('order'));
      applyDesktopRowOrder(rowItems, activeIndex);
      return;
    }

    if (swiperInstance) {
      if (swiperInstance.activeIndex !== activeIndex) {
        swiperInstance.slideTo(activeIndex, 0, false);
      }
      return;
    }

    rowItems.forEach((el) => el.style.removeProperty('order'));

    const controls = findBrandSwitcherControls(row);
    const navigation = resolveBrandSwiperNavigationFromControls(controls);
    const paginationPack = buildSwiperPaginationFromControls(controls);

    swiperInstance = new window.Swiper(row, {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 14,
      speed: 400,
      slideToClickedSlide: true,
      watchSlidesProgress: true,
      initialSlide: activeIndex,
      ...(navigation ? { navigation } : {}),
      ...(paginationPack ? { pagination: paginationPack.pagination } : {}),
      on: {
        slideChange(sw) {
          const idx = sw.activeIndex;
          if (idx === activeIndex) return;
          activeIndex = idx;
          setSwitcherActiveState(switchers, activeIndex);
          syncHeroVideo(root, switchers, activeIndex, false);
        },
      },
    });

    if (paginationPack?.fractionEl && swiperInstance) {
      const fe = paginationPack.fractionEl;
      const writeFraction = () => {
        fe.textContent = `${swiperInstance.realIndex + 1} / ${swiperInstance.slides.length}`;
      };
      swiperInstance.on('slideChange', writeFraction);
      swiperInstance.on('activeIndexChange', writeFraction);
      writeFraction();
    }
  }

  function onViewportChange() {
    if (isDesktop()) {
      destroySwiper();
      applyDesktopRowOrder(rowItems, activeIndex);
    } else {
      ensureSwiperMobile();
    }
  }

  if (isDesktop()) {
    applyDesktopRowOrder(rowItems, activeIndex);
  } else {
    ensureSwiperMobile();
  }

  setSwitcherActiveState(switchers, activeIndex);
  syncHeroVideo(root, switchers, activeIndex, true);

  mql.addEventListener('change', onViewportChange);

  scheduleIdlePreload(() => preloadHostedVideosFromSwitchers(switchers));

  row.addEventListener('click', (e) => {
    if (!isDesktop()) return;

    const anchor = e.target.closest('a[href]');
    if (anchor) {
      const href = anchor.getAttribute('href') || '';
      if (href !== '#' && href !== '' && !href.startsWith('javascript:')) {
        return;
      }
    }

    const hit = e.target.closest('[data-video-switcher-button]');
    if (!hit || !row.contains(hit)) return;

    const idx = switchers.indexOf(hit);
    if (idx < 0) return;
    if (idx === activeIndex) return;

    e.preventDefault();
    activeIndex = idx;
    applyActiveState(root, rowItems, switchers, activeIndex, false, true);
  });

  row.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const hit = e.target.closest('[data-video-switcher-button]');
    if (!hit || !row.contains(hit)) return;
    if (e.target.closest('a[href], button, input, textarea, select')) return;

    const idx = switchers.indexOf(hit);
    if (idx < 0) return;
    if (idx === activeIndex) return;

    e.preventDefault();
    activeIndex = idx;

    if (isDesktop()) {
      applyActiveState(root, rowItems, switchers, activeIndex, false, true);
      return;
    }

    setSwitcherActiveState(switchers, activeIndex);
    if (swiperInstance && swiperInstance.activeIndex !== activeIndex) {
      swiperInstance.slideTo(activeIndex, 380, false);
    }
    syncHeroVideo(root, switchers, activeIndex, false);
  });
}

function initAll() {
  const roots = document.querySelectorAll('[data-brand-video-switcher]');
  if (!roots.length) return;

  const run = () => {
    roots.forEach((root) => initBrandVideoSwitcher(root));
  };

  if (typeof window.Swiper === 'function') {
    run();
    return;
  }

  let tries = 0;
  const timer = window.setInterval(() => {
    tries += 1;
    if (typeof window.Swiper === 'function') {
      window.clearInterval(timer);
      run();
    } else if (tries > 160) {
      window.clearInterval(timer);
      run();
    }
  }, 25);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
